import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, ShoppingBag, ArrowRight, Loader2, Truck, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useLenis } from "@/lib/smooth";
import { COLORS } from "@/data/site";
import { eur } from "@/lib/api";

const IMG_BY_COLOR = Object.fromEntries(COLORS.map((c) => [c.key, c.img]));

export default function CartDrawer() {
  const { open, setOpen, items, updateQty, removeItem, count, subtotal, discount, teamDiscountApplied, shipping, total, remainingForFree, checkout, loading, TEAM_QTY } = useCart();
  const lenisRef = useLenis();

  useEffect(() => {
    const lenis = lenisRef && lenisRef.current;
    if (open) lenis && lenis.stop();
    else lenis && lenis.start();
    return () => lenis && lenis.start();
  }, [open, lenisRef]);

  const freePct = Math.min(100, (subtotal / 5000) * 100);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[70]" data-testid="cart-drawer">
          <motion.div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            className="absolute right-0 top-0 h-full w-full sm:w-[440px] bg-carbon border-l border-white/10 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div className="flex items-center gap-3">
                <ShoppingBag className="w-5 h-5 text-gold" />
                <h3 className="font-heading text-xl tracking-wide">Warenkorb ({count})</h3>
              </div>
              <button data-testid="cart-close" aria-label="Schließen" onClick={() => setOpen(false)} className="p-2 -mr-2 hover:text-gold transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-5">
                <ShoppingBag className="w-12 h-12 text-white/20" />
                <p className="text-white/50">Dein Warenkorb ist noch leer.</p>
                <Link to="/product" onClick={() => setOpen(false)} data-testid="cart-empty-shop" className="btn-primary">
                  Jetzt shoppen
                </Link>
              </div>
            ) : (
              <>
                {/* Free shipping progress */}
                <div className="px-6 pt-5">
                  <div className="flex items-center gap-2 text-xs text-white/60 mb-2">
                    <Truck className="w-4 h-4 text-gold" />
                    {remainingForFree > 0 ? (
                      <span>Noch <span className="text-white font-semibold">{eur(remainingForFree)}</span> bis zum kostenlosen Versand</span>
                    ) : (
                      <span className="text-gold font-semibold">Kostenloser Versand freigeschaltet!</span>
                    )}
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-gold transition-all duration-500" style={{ width: `${freePct}%` }} />
                  </div>
                  <div data-testid="team-discount-banner" className="mt-3 flex items-center gap-2 text-xs">
                    <Users className="w-4 h-4 text-gold shrink-0" />
                    {teamDiscountApplied ? (
                      <span className="text-gold font-semibold">10 % Teamrabatt aktiviert – ab {TEAM_QTY} Paar automatisch.</span>
                    ) : (
                      <span className="text-white/60">10 % Teamrabatt automatisch ab {TEAM_QTY} Paar.</span>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-5 space-y-4">
                  {items.map((it) => (
                    <div key={it.color} data-testid={`cart-item-${it.color}`} className="flex gap-4 border border-white/10 rounded-xl p-3 bg-ink">
                      <div className="h-20 w-20 rounded-lg overflow-hidden bg-ink border border-white/5 shrink-0">
                        <img src={IMG_BY_COLOR[it.color]} alt={it.label} loading="lazy" decoding="async" className="h-full w-full object-contain" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-semibold text-sm leading-tight">STARSOX Grip Socks</p>
                            <p className="text-white/50 text-xs mt-0.5">{it.label}</p>
                          </div>
                          <button onClick={() => removeItem(it.color)} data-testid={`cart-remove-${it.color}`} aria-label="Entfernen" className="text-white/40 hover:text-white transition-colors">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-white/15 rounded-full">
                            <button onClick={() => updateQty(it.color, it.quantity - 1)} data-testid={`cart-dec-${it.color}`} aria-label="Weniger" className="p-2 hover:text-gold transition-colors">
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="w-8 text-center text-sm tabular-nums" data-testid={`cart-qty-${it.color}`}>{it.quantity}</span>
                            <button onClick={() => updateQty(it.color, it.quantity + 1)} data-testid={`cart-inc-${it.color}`} aria-label="Mehr" className="p-2 hover:text-gold transition-colors">
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <span className="font-mono text-sm">{eur(it.quantity * 999)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-white/10 px-6 py-5 space-y-3">
                  <div className="flex justify-between text-sm text-white/60">
                    <span>Zwischensumme</span>
                    <span className="text-white" data-testid="cart-subtotal">{eur(subtotal)}</span>
                  </div>
                  {teamDiscountApplied && (
                    <div className="flex justify-between text-sm" data-testid="cart-team-discount">
                      <span className="text-gold">Teamrabatt (10 %)</span>
                      <span className="text-gold">−{eur(discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm text-white/60">
                    <span>Versand</span>
                    <span className="text-white">{shipping === 0 ? "Kostenlos" : eur(shipping)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold pt-1">
                    <span>Gesamt</span>
                    <span data-testid="cart-total">{eur(total)}</span>
                  </div>
                  <button
                    data-testid="checkout-button"
                    onClick={checkout}
                    disabled={loading}
                    className="btn-primary w-full mt-2 disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Zur Kasse <ArrowRight className="w-4 h-4" /></>}
                  </button>
                  <p className="text-center text-white/30 text-[11px] font-mono uppercase tracking-widest">inkl. MwSt. · Sichere Zahlung via Stripe</p>
                </div>
              </>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}
